import { useEffect, useState } from "react";
import { Loader2, Newspaper } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { NewsCard } from "@/components/news/NewsCard";
import { newsApi } from "@/components/news/newsUtils";

const PAGE = 12;

const Filters = ({ cats, active, onChange }) => (
  <div className="flex gap-2 overflow-x-auto pb-2" role="group" aria-label="Filter by category">
    {[{ slug: "", name: "All" }, ...cats].map((c) => (
      <button key={c.slug || "all"} type="button" onClick={() => onChange(c.slug)} aria-pressed={active === c.slug}
        className={`whitespace-nowrap border px-4 py-2 text-sm font-semibold transition-colors ${active === c.slug ? "border-navy bg-navy text-paper" : "border-navy/15 bg-white text-navy hover:border-navy"}`}
        data-testid={`news-filter-${c.slug || "all"}`}>{c.name}</button>
    ))}
  </div>
);

export default function News() {
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState("loading");

  useEffect(() => {
    newsApi.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setState("loading");
    newsApi.get("", { params: { limit: PAGE, category: cat || undefined } })
      .then((r) => { setItems(Array.isArray(r.data?.items) ? r.data.items : []); setTotal(r.data?.total ?? 0); setState("ready"); })
      .catch(() => setState("error"));
  }, [cat]);

  const more = async () => {
    setState("more");
    try {
      const r = await newsApi.get("", { params: { limit: PAGE, skip: items.length, category: cat || undefined } });
      setItems((prev) => [...prev, ...(Array.isArray(r.data?.items) ? r.data.items : [])]);
    } finally { setState("ready"); }
  };

  return (
    <>
      <Seo title="News & Events | First Responders Foundation" description="Canteen 33 deployments, Service Dog Program updates, fundraising events and community outreach from the First Responders Foundation in South Florida." path="/news" />
      <PageHero testId="news-hero" eyebrow="News & Events" lines={[{ text: "Stories From" }, { text: "The Front Line", className: "text-gold" }]}
        intro="Updates on our deployments, service dogs, fundraising events, and the people we serve across Palm Beach and Broward counties." />
      <section className="bg-paper py-16 md:py-24" data-testid="news-page">
        <div className="container-x">
          {cats.length > 0 && <Filters cats={cats} active={cat} onChange={setCat} />}
          {state === "loading" && <Loader2 className="mt-12 animate-spin text-navy" aria-label="Loading" />}
          {state === "error" && <p className="mt-12 text-sm text-slate" data-testid="news-error">News couldn't be loaded right now. Please try again later.</p>}
          {state !== "loading" && state !== "error" && (
            items.length ? (
              <>
                <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
                  {items.map((a) => <li key={a.slug}><NewsCard a={a} /></li>)}
                </ul>
                {items.length < total && (
                  <button type="button" onClick={more} disabled={state === "more"} className="btn btn-ghost mt-12" data-testid="news-load-more">
                    {state === "more" && <Loader2 size={16} className="animate-spin" />} Load More
                  </button>
                )}
              </>
            ) : (
              <div className="mt-12 flex items-start gap-4 border border-navy/10 bg-white p-8" data-testid="news-empty">
                <Newspaper className="shrink-0 text-gold-deep" aria-hidden="true" />
                <p className="text-sm text-ink/80">{cat ? "No articles in this category yet." : "News and upcoming events will appear here soon."}</p>
              </div>
            )
          )}
        </div>
      </section>
    </>
  );
}
